import { useEffect, useRef, useState } from 'react';
import type { Dispatch } from '@reduxjs/toolkit';

import {
  audioOverviewListsInvalidated,
  type AudioOverviewConfigStatus,
} from '@/slices/audioOverviews/audioOverviewsSlice';
import type { AudioOverviewConfig } from '@/types/AudioOverview';
import { loadAudioOverviewConfig } from '../hooks/useAudioOverviewConfig';

/** Focus and tab-visible refreshes are at most this frequent. */
export const AUDIO_OVERVIEW_CONFIG_FOCUS_THROTTLE_MS = 30_000;
export const AUDIO_OVERVIEW_CONFIG_RETRY_BASE_MS = 2_000;
export const AUDIO_OVERVIEW_CONFIG_RETRY_MAX_MS = 60_000;
/** setTimeout's ceiling; a later reset is re-armed by the next refresh. */
const MAX_TIMER_MS = 2_147_483_647;

/** 2 s, doubling per failed attempt, capped at 60 s. */
export const audioOverviewConfigRetryDelay = (attempt: number): number =>
  Math.min(
    AUDIO_OVERVIEW_CONFIG_RETRY_BASE_MS * 2 ** attempt,
    AUDIO_OVERVIEW_CONFIG_RETRY_MAX_MS
  );

interface UseAudioOverviewConfigUpkeepOptions {
  status: AudioOverviewConfigStatus;
  config: AudioOverviewConfig | null;
  dispatch: Dispatch;
  /** `locked` turned on mid-session: the plan lapsed. */
  onLocked?: () => void;
}

/**
 * Keeps `/audio-overviews/config` current for the whole session (ORA-735):
 *
 * - a failed load is retried with backoff instead of sticking;
 * - window focus and the tab becoming visible refresh it (throttled);
 * - it is refreshed as soon as `resetsAt` passes;
 * - when `locked` flips (the plan changed) every list is refetched, so rows
 *   lock or unlock, and turning locked calls `onLocked`.
 */
export const useAudioOverviewConfigUpkeep = ({
  status,
  config,
  dispatch,
  onLocked,
}: UseAudioOverviewConfigUpkeepOptions): void => {
  const retryAttemptRef = useRef(0);
  // Re-arms the retry even when the failed attempt's loading/error updates
  // are batched into one render (status would read `error` throughout).
  const [retryTick, setRetryTick] = useState(0);
  const lastFocusRefreshRef = useRef(0);
  const previousLockedRef = useRef<boolean | null>(null);
  const onLockedRef = useRef(onLocked);
  onLockedRef.current = onLocked;

  // Transient failures: back off and ask again.
  useEffect(() => {
    if (status !== 'error') {
      if (status === 'ready' || status === 'hidden') {
        retryAttemptRef.current = 0;
      }
      return;
    }

    const timer = setTimeout(() => {
      retryAttemptRef.current += 1;
      setRetryTick((tick) => tick + 1);
      void loadAudioOverviewConfig(dispatch);
    }, audioOverviewConfigRetryDelay(retryAttemptRef.current));

    return () => clearTimeout(timer);
  }, [dispatch, retryTick, status]);

  // Coming back to the app: counts and plan may have moved.
  useEffect(() => {
    if (status !== 'ready') return;

    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (
        now - lastFocusRefreshRef.current <
        AUDIO_OVERVIEW_CONFIG_FOCUS_THROTTLE_MS
      ) {
        return;
      }
      lastFocusRefreshRef.current = now;
      void loadAudioOverviewConfig(dispatch);
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [dispatch, status]);

  // The daily counts reset at `resetsAt`.
  const resetsAt = config?.resetsAt ?? '';

  useEffect(() => {
    if (status !== 'ready') return;
    const resetsAtMs = Date.parse(resetsAt);
    if (!Number.isFinite(resetsAtMs)) return;

    // A moment past the reset, so the server has rolled over too.
    const delay = Math.max(0, resetsAtMs - Date.now()) + 1_000;

    const timer = setTimeout(
      () => void loadAudioOverviewConfig(dispatch),
      Math.min(delay, MAX_TIMER_MS)
    );

    return () => clearTimeout(timer);
  }, [dispatch, resetsAt, status]);

  // The plan changed: refetch every list so rows lock or unlock.
  const locked = config?.locked;

  useEffect(() => {
    if (locked === undefined) return;
    const previous = previousLockedRef.current;
    previousLockedRef.current = locked;
    if (previous !== null && previous !== locked) {
      dispatch(audioOverviewListsInvalidated());
      if (locked) onLockedRef.current?.();
    }
  }, [dispatch, locked]);
};
